
import { Fragment, forwardRef } from "react";
import {
  Dialog as HeadlessDialog,
  DialogPanel,
  DialogTitle as HeadlessDialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";

// Base Dialog Root component
interface DialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  children: React.ReactNode;
}

export function Dialog({ open, onOpenChange, onClose, children }: DialogProps) {
  const handleClose = () => {
    onOpenChange?.(false);
    onClose?.();
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <HeadlessDialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </TransitionChild>

        {/* Full-screen container */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {children}
          </TransitionChild>
        </div>
      </HeadlessDialog>
    </Transition>
  );
}

// Dialog Content (the panel) - uses forwardRef for Headless UI TransitionChild compatibility
interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent({ children, className }, ref) {
    return (
      <DialogPanel
        ref={ref}
        className={clsx(
          "w-full max-w-md bg-surface rounded-xl border border-neutral-subtle shadow-xl",
          className
        )}
      >
        <div className="p-6 flex flex-col gap-4">{children}</div>
      </DialogPanel>
    );
  }
);

// Dialog Header
interface DialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogHeader({ children, className }: DialogHeaderProps) {
  return <div className={clsx("flex flex-col gap-1", className)}>{children}</div>;
}

// Dialog Title
interface DialogTitleProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogTitle({ children, className }: DialogTitleProps) {
  return (
    <HeadlessDialogTitle
      className={clsx("text-lg font-semibold text-text m-0", className)}
    >
      {children}
    </HeadlessDialogTitle>
  );
}

// Dialog Description
interface DialogDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogDescription({ children, className }: DialogDescriptionProps) {
  return (
    <p className={clsx("text-sm text-text-quiet m-0", className)}>{children}</p>
  );
}

// Dialog Footer
interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogFooter({ children, className }: DialogFooterProps) {
  return (
    <div className={clsx("flex justify-end gap-2 pt-2", className)}>
      {children}
    </div>
  );
}

export default Dialog;
